import api from "./api";

export const CHAMADO_STATUS = [
  "esperando_analise",
  "em_analise",
  "corrigindo",
  "esperando_validacao",
  "finalizada",
] as const;

export type ChamadoStatus = (typeof CHAMADO_STATUS)[number];
export type ChamadoTipo = "bug" | "duvida" | "acesso" | "sugestao";

export interface ChamadoHistoricoDto {
  id: string;
  acao: "criado" | "status_alterado" | "comentario" | "arquivado";
  descricao: string;
  autorId: string;
  autorNome: string;
  createdAt: string;
}

export interface ChamadoComentarioDto {
  id: string;
  texto: string;
  autorId: string;
  autorNome: string;
  createdAt: string;
}

export interface ChamadoDto {
  id: string;
  titulo: string;
  descricao: string;
  tipo: ChamadoTipo;
  modulo: string;
  pathname: string;
  userAgent: string;
  contexto: string;
  printPath: string | null;
  printOriginalName: string | null;
  autorId: string;
  autorNome: string;
  status: ChamadoStatus;
  comentarios: ChamadoComentarioDto[];
  historico?: ChamadoHistoricoDto[];
  createdAt: string;
  updatedAt: string;
}

export interface ChamadoArquivadoDto extends ChamadoDto {
  arquivadoAt: string;
  arquivadoPorId: string;
  arquivadoPorNome: string;
}

interface ChamadoResponse {
  success: boolean;
  chamado: ChamadoDto;
}

export interface NovoChamadoPayload {
  titulo: string;
  descricao: string;
  tipo: ChamadoTipo;
  modulo: string;
  pathname: string;
  userAgent: string;
  print?: File | null;
}

export interface ChamadosFilters {
  tipo?: ChamadoTipo | "";
  dateFrom?: string;
  dateTo?: string;
}

function filterParams(filters?: ChamadosFilters): Record<string, string> {
  return Object.fromEntries(
    Object.entries(filters ?? {}).filter((entry): entry is [string, string] => Boolean(entry[1])),
  );
}

export async function fetchChamados(filters?: ChamadosFilters): Promise<ChamadoDto[]> {
  const { data } = await api.get<{ success: boolean; chamados: ChamadoDto[] }>(
    "/api/chamados",
    { params: filterParams(filters) },
  );
  return data.chamados;
}

export async function fetchChamadoById(id: string): Promise<ChamadoDto> {
  const { data } = await api.get<ChamadoResponse>(
    `/api/chamados/${encodeURIComponent(id)}`,
  );
  return data.chamado;
}

export async function fetchChamadosArquivados(
  filters?: ChamadosFilters,
): Promise<ChamadoArquivadoDto[]> {
  const { data } = await api.get<{ success: boolean; chamados: ChamadoArquivadoDto[] }>(
    "/api/chamados/arquivados",
    { params: filterParams(filters) },
  );
  return data.chamados;
}

export async function archiveChamado(id: string): Promise<ChamadoArquivadoDto> {
  const { data } = await api.delete<{ success: boolean; chamado: ChamadoArquivadoDto }>(
    `/api/chamados/${encodeURIComponent(id)}`,
  );
  return data.chamado;
}

export async function deleteChamadosArquivados(ids: string[]): Promise<number> {
  const { data } = await api.delete<{ success: boolean; removed: number }>(
    "/api/chamados/arquivados",
    { data: { ids } },
  );
  return data.removed;
}

export async function createChamado(payload: NovoChamadoPayload): Promise<ChamadoDto> {
  const form = new FormData();
  form.append("titulo", payload.titulo);
  form.append("descricao", payload.descricao);
  form.append("tipo", payload.tipo);
  form.append("modulo", payload.modulo);
  form.append("pathname", payload.pathname);
  form.append("userAgent", payload.userAgent);
  if (payload.print) form.append("print", payload.print);

  const { data } = await api.post<ChamadoResponse>("/api/chamados", form);
  return data.chamado;
}

export async function updateChamadoStatus(
  id: string,
  status: ChamadoStatus,
): Promise<ChamadoDto> {
  const { data } = await api.patch<ChamadoResponse>(
    `/api/chamados/${encodeURIComponent(id)}/status`,
    { status },
  );
  return data.chamado;
}
